import { useState } from 'react';
import { trpc } from '../../lib/trpc';

interface BulkMediaOperationsProps {
  className?: string;
}

type EntityType = 'venue' | 'wine' | 'event' | 'user' | 'shared';

interface MediaWithUploader {
  id: string;
  cloudinaryPublicId: string;
  cloudinaryUrl: string;
  entityType: string;
  entityId: string;
  uploadedBy: {
    id: string;
    name?: string;
    email?: string;
  };
  filename: string;
  mimeType: string;
  width?: number;
  height?: number;
  sizeBytes: number;
  status: string;
  isPublished: boolean;
  tags: string[];
  caption?: string;
  createdAt: Date;
  updatedAt: Date;
}

export function BulkMediaOperations({ className = '' }: BulkMediaOperationsProps) {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>('venue');
  const [selectedMedia, setSelectedMedia] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPublished, setShowPublished] = useState(true);
  const [showUnpublished, setShowUnpublished] = useState(true);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);

  // Get all media for bulk operations
  const { data: allMedia, isPending: mediaLoading, refetch: refetchMedia } = 
    trpc.media.listAllMedia.useQuery({
      entityType: selectedEntityType,
      status: 'active', // Only show active media for bulk operations
      limit,
      offset,
    });

  // Bulk operations mutations
  const bulkPublishMutation = trpc.media.bulkTogglePublication.useMutation({
    onSuccess: () => {
      setSelectedMedia([]);
      refetchMedia();
    },
  });

  const bulkDeleteMutation = trpc.media.bulkDelete.useMutation({
    onSuccess: () => {
      setSelectedMedia([]);
      refetchMedia();
    },
  });

  const handleSelectAll = () => {
    if (!allMedia?.items) return;
    
    const mediaList = allMedia.items as unknown as MediaWithUploader[];
    if (selectedMedia.length === mediaList.length) {
      setSelectedMedia([]);
    } else {
      setSelectedMedia(mediaList.map((m) => m.id));
    }
  };

  const handleSelectMedia = (mediaId: string) => {
    setSelectedMedia(prev => 
      prev.includes(mediaId) 
        ? prev.filter(id => id !== mediaId)
        : [...prev, mediaId]
    );
  };

  const handleBulkPublish = async (isPublished: boolean) => {
    if (selectedMedia.length === 0) return;

    try {
      await bulkPublishMutation.mutateAsync({
        mediaIds: selectedMedia,
        isPublished,
      });
      
      alert(`Successfully ${isPublished ? 'published' : 'unpublished'} ${selectedMedia.length} media files.`);
    } catch (error) {
      console.error('Bulk publish failed:', error);
      alert('Bulk operation failed. Please check permissions and try again.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMedia.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to soft delete ${selectedMedia.length} media files? This action can be undone.`
    );

    if (!confirmed) return;

    try {
      await bulkDeleteMutation.mutateAsync({
        mediaIds: selectedMedia,
      });
      
      alert(`Successfully deleted ${selectedMedia.length} media files.`);
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Bulk delete failed. Some files may be referenced and cannot be deleted.');
    }
  };

  const mediaList = (allMedia?.items ?? []) as unknown as MediaWithUploader[];
  const filteredMedia = mediaList.filter((media) => {
    const matchesSearch = !searchTerm || 
      media.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.uploadedBy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      media.uploadedBy.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesPublished = (showPublished && media.isPublished) || 
                            (showUnpublished && !media.isPublished);
    
    return matchesSearch && matchesPublished;
  });

  if (mediaLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Filters and Search */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Bulk Media Operations</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity Type
            </label>
            <select
              value={selectedEntityType}
              onChange={(e) => {
                setSelectedEntityType(e.target.value as EntityType);
                setOffset(0);
                setSelectedMedia([]);
              }}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            >
              <option value="venue">Venues</option>
              <option value="wine">Wines</option>
              <option value="event">Events</option>
              <option value="user">Users</option>
              <option value="shared">Shared</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by filename or uploader..."
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-info-500 focus:ring-info-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status Filter
            </label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showPublished}
                  onChange={(e) => setShowPublished(e.target.checked)}
                  className="rounded border-gray-300 text-primary-700 focus:ring-info-500"
                />
                <span className="ml-1 text-sm text-gray-700">Published</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showUnpublished}
                  onChange={(e) => setShowUnpublished(e.target.checked)}
                  className="rounded border-gray-300 text-primary-700 focus:ring-info-500"
                />
                <span className="ml-1 text-sm text-gray-700">Unpublished</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      <div className="bg-gray-50 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Bulk Actions ({selectedMedia.length} selected)
        </h3>
        
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleSelectAll}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
          >
            {selectedMedia.length === filteredMedia.length ? 'Deselect All' : 'Select All'}
          </button>
          
          <button
            onClick={() => handleBulkPublish(true)}
            disabled={selectedMedia.length === 0 || bulkPublishMutation.isPending}
            className="px-4 py-2 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkPublishMutation.isPending ? 'Publishing...' : 'Publish Selected'}
          </button>
          
          <button
            onClick={() => handleBulkPublish(false)}
            disabled={selectedMedia.length === 0 || bulkPublishMutation.isPending}
            className="px-4 py-2 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkPublishMutation.isPending ? 'Unpublishing...' : 'Unpublish Selected'}
          </button>
          
          <button
            onClick={handleBulkDelete}
            disabled={selectedMedia.length === 0 || bulkDeleteMutation.isPending}
            className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {bulkDeleteMutation.isPending ? 'Deleting...' : 'Delete Selected'}
          </button>
        </div>
      </div>

      {/* Media List */}
      <div className="bg-gray-50 rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {selectedEntityType.charAt(0).toUpperCase() + selectedEntityType.slice(1)} Media
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {filteredMedia.length} of {allMedia?.total || 0} files shown
          </p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {filteredMedia.map((media: MediaWithUploader) => (
            <div key={media.id} className="px-6 py-4 flex items-center space-x-4">
              <input
                type="checkbox"
                checked={selectedMedia.includes(media.id)}
                onChange={() => handleSelectMedia(media.id)}
                className="rounded border-gray-300 text-primary-700 focus:ring-info-500"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-4">
                  <img
                    src={media.cloudinaryUrl}
                    alt={media.filename}
                    className="w-16 h-16 object-cover rounded"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {media.filename}
                      </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        media.isPublished 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {media.isPublished ? 'Published' : 'Unpublished'}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-500">
                      {formatBytes(media.sizeBytes)} • 
                      Uploaded: {new Date(media.createdAt).toLocaleDateString()}
                    </div>
                    
                    <div className="text-xs text-gray-400">
                      Uploader: {media.uploadedBy.name || media.uploadedBy.email}
                    </div>
                    
                    {media.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {media.tags.map((tag: string) => (
                          <span
                            key={tag}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-primary-100 text-primary-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {filteredMedia.length === 0 && (
            <div className="px-6 py-8 text-center text-gray-500">
              {mediaList.length === 0 
                ? `No ${selectedEntityType} media found.`
                : 'No media matches the current filters.'
              }
            </div>
          )}
        </div>
        
        {/* Pagination */}
        {allMedia && allMedia.total > limit && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {offset + 1} to {Math.min(offset + limit, allMedia.total)} of {allMedia.total} entries
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - limit))}
                disabled={offset === 0}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setOffset(offset + limit)}
                disabled={offset + limit >= allMedia.total}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper function to format bytes
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}